/**
 * Tiền VND. Luật: số nguyên, đơn vị đồng, không bao giờ dùng số thực.
 * Xem CLAUDE.md mục 1 và ADR-005.
 */

/** Một khoản tiền, đơn vị đồng. Luôn là số nguyên. */
export type Vnd = bigint

/** Bước làm tròn mặc định khi chia tiền: 1.000đ. */
export const DEFAULT_ROUND_STEP: Vnd = 1000n

/** Tạo Vnd từ số nguyên hoặc chuỗi số. Có phần thập phân là lỗi lập trình. */
export function vnd(value: number | string | bigint): Vnd {
  if (typeof value === 'bigint') return value
  if (typeof value === 'number') {
    if (!Number.isInteger(value)) {
      throw new Error(`Tien VND phai la so nguyen, nhan duoc: ${value}`)
    }
    return BigInt(value)
  }
  const trimmed = value.trim()
  if (!/^-?\d+$/.test(trimmed)) {
    throw new Error(`Chuoi tien khong hop le: "${value}"`)
  }
  return BigInt(trimmed)
}

/**
 * Chuỗi `s` có phải số nguyên không âm (chỉ chữ số 0-9) không — KHÔNG ném lỗi.
 * Dùng trong `.refine()` của zod ở biên API: zod 4 vẫn chạy `.refine()` khi `.regex()` đã sai,
 * nên `BigInt('abc')` trong refine ném SyntaxError -> 500 thay vì 400 (QA 02/10).
 */
export function laSoNguyenKhongAm(s: string): boolean {
  return /^\d{1,18}$/.test(s)
}

/** `s` là số nguyên không âm và là bội của 1.000đ — an toàn với chuỗi rác (xem `laSoNguyenKhongAm`). */
export function laBoiNghin(s: string): boolean {
  return laSoNguyenKhongAm(s) && BigInt(s) % DEFAULT_ROUND_STEP === 0n
}

/**
 * Làm tròn về phía 0 theo bước cho trước (mặc định 1.000đ).
 * Dùng "về phía 0" để số âm (hoàn tiền) không bị phóng đại độ lớn.
 */
export function roundDownTo(amountVnd: Vnd, stepVnd: Vnd = DEFAULT_ROUND_STEP): Vnd {
  if (stepVnd <= 0n) throw new Error('Buoc lam tron phai lon hon 0')
  const sign = amountVnd < 0n ? -1n : 1n
  const abs = amountVnd < 0n ? -amountVnd : amountVnd
  return sign * ((abs / stepVnd) * stepVnd)
}

/**
 * Nhân một khoản tiền với tỉ lệ tính bằng basis point (1% = 100bp).
 * Nhân trước, chia sau, rồi làm tròn — không bao giờ qua số thực.
 */
export function applyRateBp(baseVnd: Vnd, rateBp: number, stepVnd: Vnd = DEFAULT_ROUND_STEP): Vnd {
  if (!Number.isInteger(rateBp)) throw new Error(`rateBp phai la so nguyen, nhan: ${rateBp}`)
  return roundDownTo((baseVnd * BigInt(rateBp)) / 10000n, stepVnd)
}

/**
 * Chia một khoản tiền cho nhiều phần theo trọng số (basis point hoặc số nguyên bất kỳ).
 *
 * Bất biến bắt buộc: tổng các phần **luôn bằng đúng** số gốc.
 * Phần dư sau khi làm tròn được dồn cho phần **đầu tiên** (theo quy ước: KTV chính / dòng đầu).
 */
export function splitVnd(
  totalVnd: Vnd,
  weights: number[],
  stepVnd: Vnd = DEFAULT_ROUND_STEP,
): Vnd[] {
  if (weights.length === 0) throw new Error('Phai co it nhat mot phan de chia')
  if (weights.some((w) => !Number.isInteger(w) || w < 0)) {
    throw new Error('Trong so phai la so nguyen khong am')
  }
  const sum = weights.reduce((a, b) => a + b, 0)
  if (sum <= 0) throw new Error('Tong trong so phai lon hon 0')

  const parts = weights.map((w) => roundDownTo((totalVnd * BigInt(w)) / BigInt(sum), stepVnd))
  const assigned = parts.reduce((a, b) => a + b, 0n)
  const remainder = totalVnd - assigned
  const first = parts[0] ?? 0n
  parts[0] = first + remainder
  return parts
}

/**
 * Như `splitVnd` nhưng trọng số là **tiền** (`bigint`) — dùng chia một khoản theo tỉ lệ các số dư
 * (ví: tiền thật / tiền tặng, ADR-040) mà không đưa số dư qua `Number()`.
 *
 * Bất biến: tổng các phần bằng đúng `totalVnd`; phần dư dồn cho phần **đầu tiên**.
 */
export function splitVndTheoSoDu(
  totalVnd: Vnd,
  weightsVnd: readonly Vnd[],
  stepVnd: Vnd = DEFAULT_ROUND_STEP,
): Vnd[] {
  if (weightsVnd.length === 0) throw new Error('Phai co it nhat mot phan de chia')
  if (weightsVnd.some((w) => w < 0n)) throw new Error('Trong so phai khong am')
  const sum = weightsVnd.reduce((a, b) => a + b, 0n)
  if (sum <= 0n) throw new Error('Tong trong so phai lon hon 0')
  const parts = weightsVnd.map((w) => roundDownTo((totalVnd * w) / sum, stepVnd))
  const remainder = totalVnd - parts.reduce((a, b) => a + b, 0n)
  parts[0] = (parts[0] ?? 0n) + remainder
  return parts
}

/**
 * Thứ tự trả một hoá đơn (N2 — docs/plans/pv2-n2-coc.md): **cọc đang giữ → ví → quầy thu**, sau khi đã
 * trừ giảm giá (`phaiTraVnd` = tổng sau giảm). MỘT công thức cho màn thu tiền, `hoanTatLich` (trừ ví) và
 * `taoHoaDon` (trừ cọc) — lệch nhau là 409 AMOUNT_CHANGED.
 *
 * Bất biến (property test): mọi phần ≥ 0; `cocVnd + viVnd + quayThuVnd = phaiTraVnd`; cọc không vượt số
 * đang giữ, ví không vượt số dư; cọc dư (giữ > phải trả) ở lại sổ để quầy hoàn.
 */
export function thuTuTra(input: { phaiTraVnd: Vnd; cocGiuVnd: Vnd; viCoVnd: Vnd }): {
  cocVnd: Vnd
  viVnd: Vnd
  quayThuVnd: Vnd
} {
  if (input.phaiTraVnd < 0n) throw new Error('So phai tra khong duoc am')
  const kep = (x: Vnd, tran: Vnd) => (x <= 0n ? 0n : x < tran ? x : tran)
  const cocVnd = kep(input.cocGiuVnd, input.phaiTraVnd)
  const viVnd = kep(input.viCoVnd, input.phaiTraVnd - cocVnd)
  return { cocVnd, viVnd, quayThuVnd: input.phaiTraVnd - cocVnd - viVnd }
}

/**
 * Hiển thị tiền từ **chuỗi số nguyên đồng**: `"1250000"` -> `"1.250.000đ"`.
 *
 * Đây là hàm mà **giao diện** dùng, và nó cố ý không chạm tới `BigInt`.
 *
 * Vì sao quan trọng: `zmp build` ép đích biên dịch xuống **es2015**, nơi cú pháp số nguyên lớn
 * (`0n`) chưa tồn tại — một literal như vậy lọt vào gói là **cả bản dựng Mini App vỡ**, với
 * thông báo lỗi không nhắc gì tới tiền. Nâng đích biên dịch không cứu được, vì `zmp-cli` tự đặt
 * `target: es2015` và ghi đè cấu hình của ta.
 *
 * Và đây cũng là cách đúng theo ADR-026: tiền đi trên dây là **chuỗi**, nên phía hiển thị không
 * có lý do gì đổi nó thành số rồi đổi ngược lại.
 */
export function formatVndChuoi(soTien: string): string {
  const s = soTien.trim()
  if (!/^-?\d+$/.test(s)) throw new Error(`Khong phai so nguyen dong: "${soTien}"`)
  const negative = s.startsWith('-')
  const digits = negative ? s.slice(1) : s
  let grouped = ''
  for (let i = 0; i < digits.length; i++) {
    const fromEnd = digits.length - i
    grouped += digits[i]
    if (fromEnd > 1 && fromEnd % 3 === 1) grouped += '.'
  }
  return `${negative ? '-' : ''}${grouped}đ`
}

/**
 * Cộng các khoản tiền dạng chuỗi số nguyên đồng KHÔNG ÂM: `["250000", "400000"]` -> `"650000"`.
 *
 * Cho giao diện (tổng giá khi khách chọn nhiều dịch vụ): cùng lý do như `formatVndChuoi` — không
 * `Number` (mất chính xác, và `check:money` cấm), không `BigInt` (Mini App biên dịch về es2015).
 * Cộng từng chữ số như cộng tay. Tiền tính thật vẫn ở máy chủ; con số này chỉ để khách đọc.
 */
export function congVndChuoi(cacKhoan: readonly string[]): string {
  let tong = '0'
  for (const k of cacKhoan) {
    const s = k.trim()
    if (!/^\d+$/.test(s)) throw new Error(`Khong phai so nguyen dong khong am: "${k}"`)
    let kq = ''
    let nho = 0
    for (let i = 0; i < Math.max(s.length, tong.length) || nho > 0; i++) {
      const a = i < s.length ? s.charCodeAt(s.length - 1 - i) - 48 : 0
      const b = i < tong.length ? tong.charCodeAt(tong.length - 1 - i) - 48 : 0
      const c = a + b + nho
      kq = String(c % 10) + kq
      nho = c >= 10 ? 1 : 0
    }
    tong = kq.replace(/^0+(?=\d)/, '')
  }
  return tong
}

/**
 * Hiển thị tiền từ số nguyên lớn: `1250000n` -> `"1.250.000đ"`.
 *
 * Dùng ở **máy chủ**. Giao diện dùng `formatVndChuoi` — xem ghi chú bên trên.
 */
export function formatVnd(amountVnd: Vnd): string {
  // `toString()` đã mang sẵn dấu âm, nên chỗ này không cần một literal số nguyên lớn nào.
  return formatVndChuoi(amountVnd.toString())
}
